import React, { useEffect } from 'react';
import { Upload, FilePlus, AlertCircle, BarChart3, RefreshCw, CreditCard, HelpCircle, FileText } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchDocuments, addDocument, setLoading, setError, updateDocumentStatus } from '../store/documentSlice';
import { fetchInvoices } from '../store/invoiceSlice';
import DashboardOverview from './DashboardOverview';
import ExpiredTrialModal from './ExpiredTrialModal';
import UploadTipDialog from './UploadTipDialog';
import TemplateSelector from './TemplateSelector';
import UploadedDocReviewPopup from './dashboard/UploadedDocReviewPopup';
import type { InvoiceStatus, InvoiceDirection } from '../types';
import { config } from '../utils/config';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import '../styles/dashboard.css';

const MainContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { documents, loading, error } = useAppSelector(state => state.documents);
  const { invoices: createdInvoices } = useAppSelector(state => state.invoices);
  const [file, setFile] = React.useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null);
  // The dashboard is the landing view; users reach uploading via the "Upload New" tab.
  const [viewMode, setViewMode] = React.useState<'upload' | 'dashboard'>('dashboard');
  const [hasInitialized, setHasInitialized] = React.useState(false);
  const [showExpiredTrialModal, setShowExpiredTrialModal] = React.useState(false);

  // Upload tip dialog state
  const [showUploadTip, setShowUploadTip] = React.useState(false);
  const [hasTemplates, setHasTemplates] = React.useState<boolean | null>(null);

  // Template selector state
  const [showTemplateSelector, setShowTemplateSelector] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | null>(null);

  // Review popup state (shown after upload completes)
  const [showReviewPopup, setShowReviewPopup] = React.useState(false);
  const [pendingReviewDoc, setPendingReviewDoc] = React.useState<{ id: number; name: string } | null>(null);

  // Check for templates when component mounts
  useEffect(() => {
    const checkTemplates = async () => {
      try {
        const response = await apiClient.get('/api/templates');
        const templatesExist = response.data && response.data.length > 0;
        setHasTemplates(templatesExist);

        // Show tip if no templates and user hasn't dismissed it
        const dismissed = localStorage.getItem('upload_tip_dismissed') === 'true';
        if (!templatesExist && !dismissed && viewMode === 'upload') {
          setShowUploadTip(true);
        }
      } catch (error) {
        console.error('Failed to check templates:', error);
        setHasTemplates(false);
      }
    };

    checkTemplates();
  }, [viewMode]);

  // Fetch documents and created invoices on component mount
  useEffect(() => {
    dispatch(fetchDocuments());
    dispatch(fetchInvoices());
  }, [dispatch]);

  // Re-fetch documents when switching back to the dashboard view (in case data is
  // stale). The mount effect above already covers the first render, so only an
  // actual change of view triggers this — otherwise landing on the dashboard would
  // fire two identical requests.
  const previousViewMode = React.useRef(viewMode);
  useEffect(() => {
    const switchedToDashboard = previousViewMode.current !== 'dashboard' && viewMode === 'dashboard';
    previousViewMode.current = viewMode;
    if (switchedToDashboard && documents.length === 0) {
      dispatch(fetchDocuments());
    }
  }, [viewMode, documents.length, dispatch]);

  // Record that the first load finished, which is what reveals the view tabs.
  // The view itself is no longer chosen from the data: the dashboard is always the
  // landing view, and it has its own empty state pointing at the upload tab.
  useEffect(() => {
    if (!hasInitialized && !loading) {
      setHasInitialized(true);
    }
  }, [loading, hasInitialized]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'application/pdf') {
        dispatch(setError('Please upload a PDF file'));
        setFile(null);  // Clear the file state
        return;
    }

    setFile(selectedFile);

    // Always show template selector for better accuracy
    // Component will handle both cases (templates exist / no templates)
    setPendingFile(selectedFile);
    setShowTemplateSelector(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if the dragged file is a PDF
    const items = event.dataTransfer.items;
    if (items?.length > 0) {
        const item = items[0];
        if (item.type !== 'application/pdf') {
            // Add visual indication that file type is not allowed
            event.dataTransfer.dropEffect = 'none';
        } else {
            // Show that dropping is allowed
            event.dataTransfer.dropEffect = 'copy';
        }
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const droppedFile = event.dataTransfer.files[0];
    if (!droppedFile) return;

    if (droppedFile.type !== 'application/pdf') {
        dispatch(setError('Please upload a PDF file'));
        setFile(null);  // Clear the file state
        return;
    }

    setFile(droppedFile);

    // Always show template selector for better accuracy
    setPendingFile(droppedFile);
    setShowTemplateSelector(true);
  };

  const analyzeFile = async (pdfFile: File, templateId: number | null = null) => {
    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minute timeout for large PDFs

    try {
      // Check subscription status before uploading
      const usageCheck = await apiClient.post(config.endpoints.subscription.checkUsage, {
        action: 'document_upload'
      });

      if (!usageCheck.data.allowed) {
        if (usageCheck.data.expired || usageCheck.data.action_required === 'upgrade') {
          // Show expired trial modal
          setShowExpiredTrialModal(true);
          return;
        } else {
          // Show limit reached error
          dispatch(setError(usageCheck.data.reason || 'Upload limit reached'));
          return;
        }
      }

      // Clear any previous errors
      dispatch(setError(null));
      dispatch(setLoading(true));
      setUploadProgress(0);

      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const formData = new FormData();
      formData.append('file', pdfFile);

      // Add template_id if provided
      if (templateId !== null) {
        formData.append('template_id', templateId.toString());
      }

      // Log the request for debugging
      console.log(`Uploading to: ${config.API_URL}${config.endpoints.analysis}`);
      console.log(`File: ${pdfFile.name}, size: ${pdfFile.size}, type: ${pdfFile.type}`);
      console.log(`Template ID: ${templateId || 'auto-detect'}`);

      // Use the correct API endpoint from config with explicit mode and credentials
      const response = await fetch(`${config.API_URL}${config.endpoints.analysis}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
          // Do NOT set Content-Type header for multipart/form-data
        },
        credentials: 'include',
        mode: 'cors',
        signal: controller.signal
      });
  
      if (!response.ok) {
        // Handle common error cases
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Authentication required');
        }
        
        // Try to parse error response
        let errorMsg = `Server error (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail?.message || errorData.detail || errorMsg;
        } catch (parseError) {
          // If JSON parsing fails, use text
          const errorText = await response.text();
          if (errorText) errorMsg = errorText;
        }
        
        console.error('Server error response:', errorMsg);
        throw new Error(errorMsg);
      }
  
      // Parse the response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response format from server');
      }
      
      if (!data) {
        throw new Error('Empty response from server');
      }
  
      dispatch(addDocument(data));
      setUploadProgress(100);

      // Clear the file state
      setFile(null);

      // Refresh the documents list from the backend and wait for it to complete
      // before navigating to prevent blank page issue
      await dispatch(fetchDocuments()).unwrap();

      // Show direction/status review popup before navigating
      setPendingReviewDoc({
        id: data.metadata.document_id,
        name: data.metadata.filename,
      });
      setShowReviewPopup(true);
    } catch (error) {
      console.error('Error details:', error);

      // Provide better error messages for common failure scenarios
      let errorMessage = 'Error analyzing file';

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Analysis timed out. The document may be too large or complex. Please try a smaller file or try again later.';
        } else if (error.message === 'Failed to fetch') {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.message.includes('NetworkError') || error.message.includes('network')) {
          errorMessage = 'Network error occurred. Please check your connection and try again.';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'Connection blocked. Please refresh the page and try again.';
        } else {
          errorMessage = error.message;
        }
      }

      dispatch(setError(errorMessage));
      setUploadProgress(null);
    } finally {
      clearTimeout(timeoutId);
      dispatch(setLoading(false));
    }
  };

  const handleRefreshDocuments = () => {
    dispatch(fetchDocuments());
  };

  // Upload tip dialog handlers
  const handleDismissUploadTip = () => {
    localStorage.setItem('upload_tip_dismissed', 'true');
    setShowUploadTip(false);
  };

  // Template selector handlers
  const handleTemplateSelect = async (templateId: number | null) => {
    setSelectedTemplateId(templateId);
    if (pendingFile) {
      await analyzeFile(pendingFile, templateId);
      setPendingFile(null);
    }
  };

  const handleCreateTemplate = () => {
    // Navigate to template creation page
    navigate('/uploaded-invoices/templates/new');
  };

  const handleReviewConfirm = async (status: InvoiceStatus, direction: InvoiceDirection) => {
    if (pendingReviewDoc) {
      await dispatch(updateDocumentStatus({
        id: pendingReviewDoc.id,
        invoice_status: status,
        invoice_direction: direction,
      }));
    }
    setShowReviewPopup(false);
    setPendingReviewDoc(null);
    navigate('/uploaded-invoices');
  };

  const handleReviewClose = () => {
    setShowReviewPopup(false);
    setPendingReviewDoc(null);
    navigate('/uploaded-invoices');
  };

  return (
    <div className="w-full">
      <div className="dash-container">
        {/* View Navigation */}
        {(documents.length > 0 || hasInitialized) && (
          <div className="dash-tabs-row">
            <div className="dash-tabs">
              <button
                onClick={() => setViewMode('dashboard')}
                className={`dash-tab${viewMode === 'dashboard' ? ' active' : ''}`}
              >
                <BarChart3 />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
              <button
                onClick={() => setViewMode('upload')}
                className={`dash-tab${viewMode === 'upload' ? ' active' : ''}`}
              >
                <Upload />
                <span className="hidden sm:inline">Upload New</span>
                <span className="sm:hidden">Upload</span>
              </button>

              {/* Refresh Button */}
              <button
                onClick={handleRefreshDocuments}
                disabled={loading}
                className="dash-tab icon"
                title="Refresh documents"
              >
                <RefreshCw className={loading ? 'animate-spin' : ''} />
              </button>

              {/* User Guide Button */}
              <button
                onClick={() => navigate('/guide')}
                className="dash-tab icon"
                title="User Guide - Learn how to use the platform"
              >
                <HelpCircle />
              </button>
            </div>
          </div>
        )}

        {/* Dashboard Overview */}
        {viewMode === 'dashboard' && (
          <>
            {documents.length > 0 || createdInvoices.length > 0 ? (
              <DashboardOverview
                documents={documents}
                createdInvoices={createdInvoices}
                onDocumentSelect={(doc) => {
                  // Navigate to documents page to view the selected document
                  navigate('/uploaded-invoices');
                }}
              />
            ) : (
              <div className="dash-empty">
                {loading ? (
                  <div className="dash-loading">
                    <div className="dash-spinner" />
                    <p>Loading documents...</p>
                  </div>
                ) : (
                  <>
                    <FileText />
                    <h3>No Documents Found</h3>
                    <p>Upload your first document to see analytics</p>
                    <button onClick={() => setViewMode('upload')} className="dash-btn-primary">
                      Upload Document
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Upload Section */}
        {viewMode === 'upload' && (
          <>
            {documents.length === 0 && (
              <div className="dash-hero">
                <h1 className="font-display">Document Analysis & Insights</h1>
                <p>Upload your PDF documents and get instant insights with our advanced AI analysis</p>
              </div>
            )}

            <div className="w-full max-w-4xl mx-auto mb-8">
              <div
                className={`dash-drop${file ? ' has-file' : ''}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept=".pdf"
                  onChange={handleFileChange}
                />

                {file ? (
                  <>
                    <div className="dash-drop-icon">
                      <FilePlus />
                    </div>
                    <p className="dash-drop-title"><b>{file.name}</b></p>
                    <p className="dash-drop-hint">Click to change file</p>
                  </>
                ) : (
                  <>
                    <div className="dash-drop-icon">
                      <Upload />
                    </div>
                    <p className="dash-drop-title"><b>Click to upload</b> or drag and drop</p>
                    <p className="dash-drop-hint">PDF files only</p>
                  </>
                )}
              </div>

              {error && (
                <div className="dash-alert error">
                  <AlertCircle />
                  <div className="flex-1">
                    <p className="mb-2">{error}</p>
                    {(error.toLowerCase().includes('trial') ||
                      error.toLowerCase().includes('subscription') ||
                      error.toLowerCase().includes('upgrade') ||
                      error.toLowerCase().includes('limit reached')) && (
                      <button onClick={() => navigate('/pricing')} className="dash-btn-primary">
                        <CreditCard />
                        View Pricing Plans
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Loading State */}
            {loading && (
              <div className="dash-loading">
                <div className="dash-spinner" />
                <p>Analyzing document...</p>
                {uploadProgress !== null && (
                  <div className="dash-progress">
                    <div className="dash-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                )}
              </div>
            )}
          </>
        )}

      </div>

      {/* Expired Trial Modal */}
      <ExpiredTrialModal
        isOpen={showExpiredTrialModal}
        onClose={() => setShowExpiredTrialModal(false)}
        feature="upload documents"
      />

      {/* Upload Tip Dialog */}
      {showUploadTip && (
        <UploadTipDialog
          onClose={() => setShowUploadTip(false)}
          onDismiss={handleDismissUploadTip}
        />
      )}

      {/* Template Selector */}
      <TemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => {
          setShowTemplateSelector(false);
          setPendingFile(null);
          // `file` also has to go. Cancelling used to leave the dropzone showing
          // the filename and "Click to change file", as though the upload had
          // gone ahead.
          setFile(null);
        }}
        onSelectTemplate={handleTemplateSelect}
        onCreateTemplate={handleCreateTemplate}
      />

      {/* Direction & Status Review Popup (shown after upload completes) */}
      {pendingReviewDoc && (
        <UploadedDocReviewPopup
          isOpen={showReviewPopup}
          onClose={handleReviewClose}
          documentName={pendingReviewDoc.name}
          onStatusSelect={handleReviewConfirm}
        />
      )}
    </div>
  );
};

export default MainContent;